import { BLOCK_ICON } from '@/components/builder/layers-tree';
import { clearDragItem, writeDragItem, type BlockPreset } from '@/components/builder/drag-data';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { findNode, isContainer } from '@personalizehq/editor';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  Box,
  ChartNoAxesGantt,
  ChevronDown,
  ChevronRight,
  Columns3,
  Ellipsis,
  GalleryHorizontal,
  Image as ImageIcon,
  Layers3,
  LayoutGrid,
  List,
  MoreVertical,
  MousePointerClick,
  MoveVertical,
  Plus,
  Repeat2,
  Rows3,
  Smile,
  Type,
  Video,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';

// Palette items support both click-to-insert and native drag-to-canvas.

/** Where a click on the palette puts the new block. A container takes it as its LAST child (you
 *  clicked "into" the thing you selected); anything else takes it as its NEXT SIBLING (you cannot
 *  nest a block inside a `text`). Nothing selected → the end of the root. */
export function insertionPoint(root: BlockNode, selection: string | null): { parentId: string; index: number } {
  const hit = selection === null ? undefined : findNode(root, selection);
  if (!hit) return { parentId: root.id, index: isContainer(root) ? root.children.length : 0 };
  if (isContainer(hit.node)) return { parentId: hit.node.id, index: hit.node.children.length };
  // A non-container that is somehow the root (a single `text` document) has nowhere to put a
  // sibling; the caller's insert would throw. Refuse by targeting the root itself.
  if (!hit.parent) return { parentId: root.id, index: 0 };
  return { parentId: hit.parent.id, index: hit.index + 1 };
}

// RANKED, NOT RESTRICTED. Twelve blocks presented with equal weight reads as an invitation to put
// a progress bar in a confirmation sheet — but that is a hierarchy problem, not a permissions one.
// A surface-scoped allowlist would invent a law the IR does not have: `progress` in a bottom sheet
// is legal and renderable (a two-step permission sheet with segmented dots is a real design), and
// blocking it leaves the author who wants one with no path at all. So the rare blocks are demoted,
// not removed. `custom` and `repeat` in particular are power tools — a `custom` needs a host-side
// registration to render anything, and `repeat` only expands against the SDK's campaign list.
export const GROUPS: Array<{ title: string; items: readonly BlockPreset[]; collapsed?: boolean }> = [
  {
    title: 'Common',
    items: [
      { label: 'Vertical stack', kind: 'stack', defaults: { direction: 'column' } },
      // A horizontal stack fills its width by default: the commonest thing an author does next is
      // `justify` (space between / distribute), and that is INERT on a hugging main axis (there is no
      // free space along it). A hugging row lands them in the exact dead state `inertJustify` has to
      // apologise for. A vertical stack keeps hugging — its main axis is height, and a stack that
      // grabbed all vertical space by default would be the more surprising wrong. See Field `inert`.
      { label: 'Horizontal stack', kind: 'stack', defaults: { direction: 'row', size: { width: 'fill' } } },
      { label: 'Layer', kind: 'box' },
      { label: 'Text', kind: 'text' },
      { label: 'Button', kind: 'button' },
      { label: 'Image', kind: 'image' },
      // Not a block — SPEC §5.12 cut `icon` as a COMPOSITION, and this is it: an `image` that has
      // been through the icon picker. It sits next to Image because that is what an author is
      // choosing between, not next to the power tools.
      { label: 'Icon', kind: 'image', picks: 'icon' },
      { label: 'Spacer', kind: 'spacer' },
    ],
  },
  {
    title: 'Layout',
    items: [
      { label: 'Vertical scroll', kind: 'scroll', defaults: { axis: 'vertical' } },
      { label: 'Horizontal pages', kind: 'scroll', defaults: { axis: 'horizontal', snap: true } },
      { label: 'Grid', kind: 'grid' },
    ],
  },
  {
    title: 'Advanced',
    items: [
      { label: 'Video', kind: 'video' },
      { label: 'Progress', kind: 'progress' },
      {
        label: 'Carousel indicator',
        kind: 'progress',
        defaults: { gap: 6, size: { width: 'fill', height: 8 } },
        requires: 'trackable_scroll',
      },
      { label: 'Custom component', kind: 'custom' },
      { label: 'Repeating content', kind: 'repeat' },
    ],
    collapsed: true,
  },
];

/** Presets can share an IR block kind while representing different authoring concepts. Their
 *  palette icons must describe the preset, not merely repeat the underlying wire type. */
export const PRESET_ICON: Record<string, LucideIcon> = {
  'Vertical stack': Rows3,
  'Horizontal stack': Columns3,
  Layer: Layers3,
  Text: Type,
  Button: MousePointerClick,
  Image: ImageIcon,
  Icon: Smile,
  Spacer: MoveVertical,
  'Vertical scroll': List,
  'Horizontal pages': GalleryHorizontal,
  Grid: LayoutGrid,
  Video,
  Progress: ChartNoAxesGantt,
  'Carousel indicator': Ellipsis,
  'Custom component': Box,
  'Repeating content': Repeat2,
};

interface PaletteProps {
  onInsert: (preset: BlockPreset) => void;
  /** `true` = insertable; a string = disabled, and the string is WHY (shown as the tooltip). Two
   *  reasons today: the step is at the node limit, or Carousel indicator has no pages to track. */
  canInsert?: (preset: BlockPreset) => true | string;
  pickerOpen?: boolean;
  onPickerOpenChange?: (open: boolean) => void;
  pickerLabel?: string;
}

/** The canvas owns component insertion. Common + layout blocks stay one click away in a compact,
 *  viewport-pinned left rail; uncommon power tools live behind one labelled overflow button. This
 *  keeps Layers for document structure and the inspector on the right for editing selected blocks. */
export function FloatingPalette({
  onInsert,
  canInsert = () => true,
  pickerOpen,
  onPickerOpenChange,
  pickerLabel = 'Insert at end of step',
}: PaletteProps): React.JSX.Element {
  const primaryGroups = GROUPS.slice(0, 2);
  const advanced = GROUPS[2]!.items;

  return (
    <TooltipProvider delayDuration={250}>
      <PopoverPrimitive.Root
        {...(pickerOpen === undefined ? {} : { open: pickerOpen })}
        {...(onPickerOpenChange ? { onOpenChange: onPickerOpenChange } : {})}
      >
        <PopoverPrimitive.Anchor asChild>
          <div
            role="toolbar"
            aria-label="Components"
            aria-orientation="vertical"
            className="pointer-events-auto flex max-h-full flex-col items-center rounded-xl border bg-background/95 p-1.5 shadow-xl backdrop-blur"
          >
        <button
          type="button"
          aria-label="Choose component"
          title="Choose component"
          onClick={() => onPickerOpenChange?.(true)}
          className="grid size-9 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Plus className="size-4" />
        </button>
        <div className="flex min-h-0 flex-col items-center overflow-y-auto overscroll-contain">
          {primaryGroups.map((group, index) => (
            <div
              key={group.title}
              aria-label={group.title}
              className={`flex flex-col items-center gap-0.5 ${index === 0 ? '' : 'mt-1 border-t pt-1'}`}
            >
              {group.items.map((item) => (
                <DockItem key={item.label} item={item} onInsert={onInsert} canInsert={canInsert} />
              ))}
            </div>
          ))}
        </div>

        <div className="mt-1 border-t pt-1">
          <PopoverPrimitive.Root>
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                aria-label="More components"
                title="More components"
                className="grid size-9 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <MoreVertical className="size-4" />
              </button>
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                side="right"
                align="end"
                sideOffset={10}
                className="z-50 w-64 rounded-xl border bg-popover p-2 text-popover-foreground shadow-xl"
              >
                <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Advanced
                </p>
                <div className="grid grid-cols-3 gap-1">
                  {advanced.map((item) => {
                    const Icon = PRESET_ICON[item.label] ?? BLOCK_ICON[item.kind];
                    const verdict = canInsert(item);
                    const enabled = verdict === true;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        draggable={enabled}
                        disabled={!enabled}
                        aria-label={`Insert ${item.label}`}
                        title={enabled ? `Insert ${item.label}` : verdict}
                        onClick={() => onInsert(item)}
                        onDragStart={(event) => writeDragItem(event.dataTransfer, { type: 'block', preset: item })}
                        onDragEnd={clearDragItem}
                        className="flex cursor-grab flex-col items-center gap-1 rounded-lg px-1 py-2 text-center text-[10px] leading-tight text-muted-foreground hover:bg-secondary hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Icon className="size-4" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
                <PopoverPrimitive.Arrow className="fill-popover" />
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        </div>
          </div>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side="right"
            align="start"
            sideOffset={10}
            className="z-50 w-72 rounded-xl border bg-popover p-1 text-popover-foreground shadow-xl"
          >
            <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {pickerLabel}
            </p>
            <Palette
              onInsert={(preset) => {
                onInsert(preset);
                onPickerOpenChange?.(false);
              }}
              canInsert={canInsert}
            />
            <PopoverPrimitive.Arrow className="fill-popover" />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </TooltipProvider>
  );
}

function DockItem({
  item,
  onInsert,
  canInsert,
}: {
  item: BlockPreset;
  onInsert: (preset: BlockPreset) => void;
  canInsert: (preset: BlockPreset) => true | string;
}): React.JSX.Element {
  const Icon = PRESET_ICON[item.label] ?? BLOCK_ICON[item.kind];
  const verdict = canInsert(item);
  const enabled = verdict === true;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          draggable={enabled}
          disabled={!enabled}
          aria-label={`Insert ${item.label}`}
          title={enabled ? undefined : verdict}
          onClick={() => onInsert(item)}
          onDragStart={(event) => writeDragItem(event.dataTransfer, { type: 'block', preset: item })}
          onDragEnd={clearDragItem}
          className="grid size-9 shrink-0 cursor-grab place-items-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Icon className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {enabled ? item.label : verdict}
      </TooltipContent>
    </Tooltip>
  );
}

export function Palette({ onInsert, canInsert = () => true }: PaletteProps): React.JSX.Element {
  return (
    <div className="p-2">
      {GROUPS.map((group) => (
        <PaletteGroup key={group.title} group={group} onInsert={onInsert} canInsert={canInsert} />
      ))}
    </div>
  );
}

function PaletteGroup({
  group,
  onInsert,
  canInsert,
}: {
  group: (typeof GROUPS)[number];
  onInsert: (preset: BlockPreset) => void;
  canInsert: (preset: BlockPreset) => true | string;
}): React.JSX.Element {
  const [open, setOpen] = useState(!group.collapsed);

  return (
    <div className="mb-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1 px-1 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {group.title}
        {open ? null : <span className="ml-auto tabular-nums">{group.items.length}</span>}
      </button>

      {open ? (
        <div className="grid grid-cols-3 gap-1">
          {group.items.map((item) => {
            const Icon = PRESET_ICON[item.label] ?? BLOCK_ICON[item.kind];
            const verdict = canInsert(item);
            const enabled = verdict === true;
            return (
              <button
                key={item.label}
                type="button"
                draggable={enabled}
                disabled={!enabled}
                aria-label={`Insert ${item.label}`}
                title={enabled ? `Insert ${item.label}` : verdict}
                onClick={() => onInsert(item)}
                onDragStart={(e) => writeDragItem(e.dataTransfer, { type: 'block', preset: item })}
                onDragEnd={clearDragItem}
                className="flex cursor-grab flex-col items-center gap-1 rounded-md border border-transparent px-1 py-2 text-[11px] text-muted-foreground transition-colors hover:border-border hover:bg-secondary/60 hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon className="size-4" />
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
